import { FulfillmentStepKind, InternalConfirmationState, OrderStatus } from "client/api-v2";
import type { OrderSummaryDto } from "client/api-v2";

export type FulfilmentStatusSource = Pick<OrderSummaryDto, "status" | "internalConfirmation" | "fulfillment">;

export interface FulfilmentStatusExplanation {
  headline: string;
  detail?: string;
}

export function describeFulfilmentStatus(order: FulfilmentStatusSource): FulfilmentStatusExplanation {
  switch (order.status) {
    case OrderStatus.New:
      return {
        headline: "Not yet issued",
        detail: "This quote is still a draft. A license issues only after it is published and the customer accepts it.",
      };
    case OrderStatus.Quote:
      return {
        headline: "Awaiting acceptance",
        detail: "The customer has not accepted this quote yet. A licence issues only once they do.",
      };
    case OrderStatus.Cancelled:
      return {
        headline: "Cancelled",
        detail: "This order was cancelled before acceptance. No licence issues from a cancelled order.",
      };
    case OrderStatus.Expired:
      return {
        headline: "Expired",
        detail:
          "The quote's validity window passed before the customer accepted it. No licence issues from an expired quote.",
      };
    case OrderStatus.Accepted:
      return describeAcceptedFulfilmentStatus(order);
    default:
      throw new Error(`Unrecognised order status: ${order.status}`);
  }
}

function describeAcceptedFulfilmentStatus(order: FulfilmentStatusSource): FulfilmentStatusExplanation {
  if (order.internalConfirmation.state === InternalConfirmationState.AwaitingConfirmation) {
    return {
      headline: "Accepted, awaiting internal confirmation",
      detail: order.internalConfirmation.note
        ? `Staff must confirm this order before billing setup or provisioning can start. Reason given: "${order.internalConfirmation.note}"`
        : "Staff must confirm this order before billing setup or provisioning can start.",
    };
  }

  if (waitsForFulfilmentApproval(order)) {
    return {
      headline: "Accepted, awaiting fulfilment approval",
      detail: "A staff member must approve fulfilment before billing setup or provisioning can start.",
    };
  }

  return { headline: "Accepted" };
}

// Why: Swashbuckle drops `nullable` from a $ref, so `fulfillment` is typed non-null while the API can send null.
function waitsForFulfilmentApproval(order: FulfilmentStatusSource): boolean {
  const outstanding = order.fulfillment?.outstandingRequiredSteps ?? [];
  return outstanding.includes(FulfillmentStepKind.ManualGate);
}
